import parse from "html-react-parser";
import sanitizeHtml from "sanitize-html";
import { useWebSocket } from "../context/WebSocketContext";
import {
  AgentMsg,
  BotMsg,
  MessageWrapper,
  Sender,
  Time,
  UserMsg,
} from "./styles/ChatStyles";
import { useClientConfig } from "../hooks/useClientConfig";
import { replaceWeChatEmojiTags } from "../utils/replaceWeChatEmojis";
import { debugLog } from "../utils/debug";
import { useTheme } from "styled-components";
import { useState } from "react";
import IframeModal from "./IframeModal";


const Message = ({
  from,
  text,
  time,
  senderNickname,
  grouped,
  sourceType,
  mediaType,
  mediaFileType,
}) => {
  const config = useClientConfig();
  const [clickedButtons, setClickedButtons] = useState(() => new Set());
  const [iframeUrl, setIframeUrl] = useState(null);


  const botName = config?.botName || "Assistente";
  const { setMessages, sendPayload } = useWebSocket();
  const theme = useTheme(); // ✅ acessa o theme atual

  const isUser = from === "user";
  const isSystem =
    from === "system" ||
    from === "bot" ||
    sourceType === "ROBOT" ||
    sourceType === "SYSTEM" ||
    (senderNickname && ["sofi", "sistema"].includes(senderNickname.toLowerCase()));

  const align = isUser ? "right" : "left";
  const Bubble = isSystem ? BotMsg : isUser ? UserMsg : AgentMsg;

  const getSenderKey = () => {
    const nick = senderNickname?.toLowerCase() || "";
    const result =
      from === "system" ||
        from === "bot" ||
        sourceType === "ROBOT" ||
        sourceType === "SYSTEM" ||
        nick.includes("sofi") ||
        nick === "sistema"
        ? "ROBOT"
        : sourceType === "AGENT"
          ? "agent"
          : isUser
            ? "user"
            : "outro";

    debugLog("👤 Sender key:", result, { from, sourceType, senderNickname });
    return result;
  };

  const getDisplayName = () => {
    const key = getSenderKey();
    if (isSystem) return botName;
    if (key === "agent") return senderNickname || "Agente";
    if (key === "user") return "Você";
    return senderNickname || "Outro";
  };

  const getAvatar = () => {
    const key = getSenderKey();
    if (key === "ROBOT") return "/images/avatar.png";
    if (key === "agent") return "/images/atendente.png";
    return null;
  };

  const decodeUnicode = (str) => {
    try {
      return JSON.parse(`"${str}"`);
    } catch {
      return str;
    }
  };

  const handleButtonClick = (label) => {
    const value = label.trim();

    setClickedButtons((prev) => {
      const next = new Set(prev);
      next.add(value);
      return next;
    });

    debugLog("🖱️ Botão clicado:", value);

    const timestamp = Date.now();
    const formattedTime = new Date(timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

    setMessages((prev) => [
      ...prev,
      {
        from: "user",
        text: value,
        time: formattedTime,
        timestamp,
      },
    ]);
    sendPayload("send_message", { texto: value });
  };

  const renderContent = () => {
    let content = decodeUnicode(text || "");
    content = replaceWeChatEmojiTags(content);
    debugLog("🧾 Conteúdo renderizado:", content, { mediaType });

    switch (mediaType) {
      case "IMAGE":
      case "FILE_IMAGE":
        return <img src={content} alt="imagem enviada" style={{ maxWidth: "100%", borderRadius: 8 }} />;

      case "AUDIO":
        return (
          <audio controls style={{ width: "100%" }}>
            <source src={content} type={`audio/${mediaFileType || "mpeg"}`} />
            Seu navegador não suporta áudio.
          </audio>
        );

      case "VIDEO":
        return (
          <video controls style={{ maxWidth: "100%", borderRadius: 8 }}>
            <source src={content} type={`video/${mediaFileType || "mp4"}`} />
            Seu navegador não suporta vídeo.
          </video>
        );

      case "FILE":
      case "DOCUMENT":
        return (
          <a
            href={content}
            download
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#2a7ae4",
              textDecoration: "underline",
              wordBreak: "break-all",
              display: "block",
            }}
          >
            📎 Baixar arquivo
          </a>
        );

      default: {
        const iframeLinks = config?.iframeLinks || {};
        let matchedIframeUrl = null;
        let contentWithoutKey = content;

        for (const [key, value] of Object.entries(iframeLinks)) {
          const idx = content.indexOf(key);
          if (idx === -1) continue;

          const configUrl = typeof value === "string" ? value : value?.url;
          const configParams = typeof value === "object" && value?.params ? value.params : null;

          let matchedString = key;
          let paramValues = [];

          const afterKey = content.substring(idx + key.length);
          if (afterKey.startsWith("|")) {
            const rest = afterKey.slice(1);
            const rawParts = rest.split("|");
            const parts = [];
            for (const p of rawParts) {
              const trimmed = p.trim();
              if (trimmed.includes(" ")) {
                parts.push(trimmed.split(/\s/)[0]);
                break;
              }
              if (trimmed) parts.push(trimmed);
            }
            paramValues = configParams?.length ? parts.slice(0, configParams.length) : parts;
            matchedString = key + "|" + parts.join("|");
          }

          if (configUrl) {
            let finalUrl = configUrl;
            if (configParams?.length && paramValues.length) {
              const query = configParams
                .map((name, i) => `${name}=${encodeURIComponent(paramValues[i] || "")}`)
                .join("&");
              finalUrl = configUrl.includes("?") ? `${configUrl}&${query}` : `${configUrl}?${query}`;
            }
            matchedIframeUrl = finalUrl;
            contentWithoutKey = content.replace(matchedString, "").trim();
            break;
          }
        }

        if (matchedIframeUrl) {
          const sanitizedRest = contentWithoutKey
            ? sanitizeHtml(contentWithoutKey, {
                allowedTags: ["b", "i", "em", "strong", "u", "br", "p", "span"],
                allowedAttributes: { "*": ["style"] },
              })
            : "";

          return (
            <>
              <style>
                {`
                  .button-mensagem {
                    margin: 0.3rem;
                    width:100%;
                    padding: 1.3em 3em;
                    font-size: 12px;
                    text-transform: uppercase;
                    letter-spacing: 1.5px;
                    font-weight: 500;
                    color: #000;
                    background-color: #fff;
                    border: none;
                    border-radius: 1.5rem;
                    transition: all 0.5s ease 0s;
                    cursor: pointer;
                    outline: none;
                  }

                  .button-mensagem:hover {
                    background-color: ${theme.primary || "#000"};
                    color:#fff;
                  }
                `}
              </style>

              {sanitizedRest && <div>{parse(sanitizedRest)}</div>}
              <button
                className="button-mensagem"
                onClick={() => setIframeUrl(matchedIframeUrl)}
              >
                DASHBOARD
              </button>
              {iframeUrl && (
                <IframeModal url={iframeUrl} onClose={() => setIframeUrl(null)} />
              )}
            </>
          );
        }

        const sanitized = sanitizeHtml(content, {
          allowedTags: ["b", "i", "em", "strong", "u", "br", "ul", "li", "ol", "p", "span", "div", "a", "button"],
          allowedAttributes: {
            a: ["href", "target", "rel", "style"],
            button: ["style"],
            "*": ["style"],
          },
          transformTags: {
            a: sanitizeHtml.simpleTransform("a", {
              target: "_blank",
              rel: "noopener noreferrer",
            }),
          },
        });

        return (
          <>
            <style>
              {`
                .button-mensagem {
                  margin: 0.3rem;
                  width:100%;
                  padding: 1.3em 3em;
                  font-size: 12px;
                  text-transform: uppercase;
                  letter-spacing: 1.5px;
                  font-weight: 500;
                  color: #000;
                  background-color: #fff;
                  border: none;
                  border-radius: 1.5rem;
                  transition: all 0.5s ease 0s;
                  cursor: pointer;
                  outline: none;
                }

                .button-mensagem:hover {
                  background-color: ${theme.primary || "#000"};
                  color:#fff;
                }
              `}
            </style>

            {parse(sanitized, {
              replace: (domNode) => {
                if (domNode.name === "button" && domNode.children?.[0]?.data) {
                  const label = domNode.children[0].data;
                  const isClicked = clickedButtons.has(label);

                  return (
                    <button
                      className="button-mensagem"
                      onClick={() => handleButtonClick(label)}
                      style={{
                        backgroundColor: isClicked ? theme.primary : undefined,
                        color: isClicked ? (theme.buttonText || "#fff") : undefined,
                      }}
                    >
                      <span className="btn-txt">{label}</span>
                    </button>
                  );
                }
              },
            })}
          </>
        );

      }
    }
  };

  return (
    <MessageWrapper align={isSystem ? "center" : align} $grouped={grouped}>
      {!grouped && (
        <Sender>
          {(getSenderKey() === "ROBOT" || getSenderKey() === "agent") && (
            <img
              src={getAvatar()}
              alt={getDisplayName() + " avatar"}
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                objectFit: "cover",
              }}
            />
          )}
          <span>{getDisplayName()}</span>
        </Sender>
      )}
      <Bubble $grouped={grouped}>
        {renderContent()}
        <Time>{time}</Time>
      </Bubble>
    </MessageWrapper>
  );
};

export default Message;